import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderData } from '@/hooks/useHeaderData';
import { PREF_KEYS, usePreference } from '@/hooks/useUserPreferences';
import { playNotificationChime } from '@/lib/notificationChime';

export interface AppNotification {
  id: string;
  category: string;
  title: string;
  body: string | null;
  link_path: string | null;
  metadata: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
}

export const NOTIFICATIONS_KEY = ['notifications'];

interface NotificationsPayload {
  items?: Array<{
    id: string;
    kind?: string;
    title: string;
    body: string | null;
    href?: string | null;
    metadata: Record<string, unknown> | null;
    read_at: string | null;
    created_at: string;
  }>;
  unread_count?: number;
}

/** In-app inbox: the variable reward surface for both personas. */
export function useNotifications(limit = 30) {
  const { user } = useAuth();
  const { data: headerData } = useHeaderData();
  const companyId = headerData?.companyId ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, limit],
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async (): Promise<AppNotification[]> => {
      const { data, error } = await supabase.rpc('get_notifications', { p_limit: limit });
      if (error) throw error;
      // The RPC returns { items, unread_count } — never a bare array.
      const payload = (data ?? {}) as unknown as NotificationsPayload;
      return (payload.items ?? []).map((n) => ({
        id: n.id,
        category: n.kind ?? 'system',
        title: n.title,
        body: n.body ?? null,
        link_path: n.href ?? null,
        metadata: n.metadata ?? null,
        read_at: n.read_at ?? null,
        created_at: n.created_at,
      }));
    },
  });

  /* Realtime: alerts are COMPANY-addressed — `match_buyer_watches` inserts rows with
     `user_id` NULL — so filtering on `user_id` could never match the very rows that
     matter. Subscribe on company_id and let `get_notifications` do the user scoping
     it already does. */
  const [soundOn] = usePreference<boolean>(PREF_KEYS.notificationSound, true);
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  useEffect(() => {
    if (!user || !companyId) return;
    const channel = supabase
      .channel(`notifications-company-${companyId}-${limit}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `company_id=eq.${companyId}` },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
          const row = payload.new as { user_id?: string | null } | null;
          // One ding per arrival for this person — never a repeat reminder.
          if (soundRef.current && (!row?.user_id || row.user_id === user.id)) playNotificationChime();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, companyId, queryClient, limit]);

  /* Unknown ≠ all caught up. A failed fetch must never render a confident "0". */
  const unreadCount = query.isError || query.data === undefined
    ? null
    : query.data.filter((n) => !n.read_at).length;

  return { ...query, unreadCount };
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) => {
      const { error } = await supabase.rpc('mark_notifications_read', {
        p_ids: ids ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}